import { useState, useEffect, useCallback } from 'react'
import { CheckoutProgress } from '../CheckoutProgress'
import styles from './AnnualUpgrade.pcss'
import Button from '@mui/material/Button'
import { Navigate, useLocation, useNavigate } from 'react-router'
import { CURRENCY_SIGN_MAP } from 'shared'
import { useDispatch, useSelector } from 'react-redux'
import { StoreThunkDispatch } from 'store/state'
import { upgradeProduct } from 'services/product'
import { currentUserSelector } from 'services/users/selectors'
import { message } from 'services/snackbar'
import { NOTIFICATION_DURATION_LONG } from 'components/ConnectedSnackbar'
import { useIntl } from 'react-intl'
import { MS_IN_MINUTE } from 'utils/constants'

export function AnnualUpgrade() {
  const location = useLocation()
  const navigate = useNavigate()
  const intl = useIntl()
  const user = useSelector(currentUserSelector)
  const dispatch = useDispatch<StoreThunkDispatch>()
  const [loading, setLoading] = useState(false)
  const { monthlyPrice, annualPrice, currency, handle, ott, productName } = location.state || {}

  const goToLogin = useCallback(() => {
    navigate(`/login${ott ? '?ott=' + ott : ''}`)
  }, [ott, navigate])

  // EXPL: Redirect to login page after 5 minutes if user doesn't accept the offer
  useEffect(() => {
    const t = setTimeout(goToLogin, 5 * MS_IN_MINUTE) // eslint-disable-line no-magic-numbers
    return () => {
      clearTimeout(t)
    }
  }, [goToLogin])

  if (!monthlyPrice || !annualPrice || !currency || !handle || !productName) {
    return <Navigate to="/signup/payment-details" />
  }

  /* eslint-disable no-magic-numbers */
  const annualOff = (monthlyPrice - (annualPrice / 12)) / monthlyPrice
  const discount = Math.round(annualOff * 100)
  /* eslint-enable no-magic-numbers */

  const accept = () => {
    setLoading(true)
    dispatch(upgradeProduct(handle)).subscribe(
      () => {
        dispatch(message(intl.formatMessage(
          { id: 'notifications.upgrade-success' },
          { subscription: productName.toUpperCase() }
        ), 'success'))
        goToLogin()
      },
      error => {
        setLoading(false)
        if (error.isChargify) {
          dispatch(message(intl.formatMessage(
            { id: 'upgrade-popup.notification-self-service' },
            {
              a: (text: string) => (
                <a href={user?.account.selfServicePage} target="_blank">{text}</a>
              )
            } as any
          ), 'error', NOTIFICATION_DURATION_LONG))
        } else {
          dispatch(message(intl.formatMessage({ id: 'notifications.error-w-message' }, { error: error.message }) as string, 'error'))
        }
      }
    )
  }

  return (
    <div>
      <CheckoutProgress activeStep="annual-upgrade" />
      <div className={styles.main}>
        <h1 className={styles.title}>You're in! Want a lower price?</h1>
        <h3 className={styles.subtitle}>Lock in the lowest price by moving to an annual plan</h3>
        <div className={styles.imgBox}>
          <div className={styles.imgLayout}>
            <div className={styles.imgCol1}>
              <img src="/static/img/signup/coin_stack_tall.png" />
              <div className={styles.imgLabel}>Monthly price</div>
              <span className={styles.price}>{`${CURRENCY_SIGN_MAP[currency]}${monthlyPrice}/mo`}</span>
            </div>
            <div className={styles.imgCol2}>
              <img src="/static/img/signup/green_arrow.png" />
              <div className={styles.imgLabel}></div>
              <span className={styles.price}></span>
            </div>
            <div className={styles.imgCol3}>
              <img src="/static/img/signup/coin_stack_short_save.png" />
              <div className={styles.imgLabel}>Annual price</div>
              {/* eslint-disable-next-line no-magic-numbers */}
              <span className={styles.price}>{`${CURRENCY_SIGN_MAP[currency]}${annualPrice / 12}/mo`}</span>
            </div>
          </div>
        </div>
        <p className={styles.text}>82% of customer choose annual to get the lowest price</p>

        <Button color="primary" variant="contained" className={styles.btn} disabled={loading} onClick={accept}>
          {`Choose Annual Plan and save ${discount}%`}
        </Button>
        <span className={styles.btnDecline} onClick={goToLogin}>
          No thanks, I'll stick with the higher priced monthly plan
        </span>
      </div>
    </div>
  )
}
